/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService, ApiResponse } from '../core';

/**
 * User interface
 */
export interface User {
  id: string;
  email: string;
  name: string;
  bio?: string;
  avatar?: string;
  phone_number?: string;
  role?: number;
  is_email_verified?: boolean;
  two_factor_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * User Profile Update interface
 */
export interface UserProfileUpdate {
  name?: string;
  bio?: string;
  phoneNumber?: string;
  avatar?: string;
  contentAffirmationDate?: string | null;
  contentAffirmationTermsVersion?: string | null;
}

/**
 * User Dashboard interface
 */
export interface UserDashboard {
  summary: {
    organizationsCount: number;
    teamsCount: number;
    pendingInvitationsCount: number;
    membershipsCount: number;
  };
  recentActivity: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
  }>;
  organizations: any[];
  teams: any[];
  pendingInvitations: any[];
}

/**
 * User Stats interface
 */
export interface UserStats {
  organizationsCount: number;
  teamsCount: number;
  membershipsCount: number;
  pendingInvitationsCount: number;
}

/**
 * User Membership interface
 */
export interface UserMembership {
  id: string;
  userId: string;
  organizationId: string;
  organizationName?: string;
  teamId?: string;
  teamName?: string;
  roleId: string;
  roleName?: string;
  joinedAt?: string;
  invitedBy?: string;
}

/**
 * User Permission interface
 */
export interface UserPermission {
  id: string;
  name: string;
  resource: string;
  action: string;
  scope?: string;
  description?: string;
}

/**
 * User Invitation interface
 */
export interface UserInvitation {
  id: string;
  organization_id: string;
  organization_name?: string;
  team_id?: string;
  team_name?: string;
  invitee_email: string;
  inviter_id: string;
  inviter_name?: string;
  role_id: string;
  role_name?: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  personal_message?: string;
  expires_at?: string;
  created_at?: string;
}

/**
 * User Deletion Blocker interface
 */
export interface UserDeletionBlocker {
  type: 'organization' | 'team';
  entityId: string;
  entityName: string;
  role: string;
  reason: string;
}

/**
 * User Deletion Validation interface
 */
export interface UserDeletionValidation {
  canDelete: boolean;
  blockers: UserDeletionBlocker[];
  warnings: string[];
  dependentEntities: {
    memberships: number;
    teams: number;
    organizations: number;
  };
}

/**
 * Content Affirmation Status interface
 */
export interface ContentAffirmationStatus {
  hasAffirmed: boolean;
  requiresReaffirmation: boolean;
  reason: string;  // '', 'never_affirmed', 'expired', 'terms_updated'
  affirmationDate?: string;
  termsVersion?: string;
}

/**
 * User Service
 *
 * Handles all user-related operations including:
 * - User profile management
 * - User dashboard and statistics
 * - User organizations and teams
 * - User memberships and permissions
 * - User invitations
 * 
 * Extracted from the monolithic API service.
 */
export class UserService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  // ===== USER PROFILE OPERATIONS =====

  /**
   * Get user profile by ID
   */
  async getUserById(userId: string): Promise<User> {
    try {
      console.log(`Fetching user profile: ${userId}`);
      
      const response = await this.getCached<any>(
        `/users/${userId}`,
        undefined,
        { timeout: 10000 }
      );
      
      return this.normalizeUser(response);
    } catch (error) {
      console.error(`Error fetching user ${userId}:`, error);
      throw this.errorHandler.handleError(error, `UserService.getUserById(${userId})`);
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UserProfileUpdate): Promise<{ success: boolean; user: User }> {
    try {
      console.log('Updating user profile:', data);
      
      const response = await this.put<any>('/users/me', data);
      
      // Invalidate cache
      this.invalidateCache('user');
      
      return {
        success: true,
        user: this.normalizeUser(response.user || response)
      };
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw this.errorHandler.handleError(error, 'UserService.updateProfile');
    }
  }

  // ===== USER DASHBOARD OPERATIONS =====

  /**
   * Get user dashboard with all key information
   */
  async getUserDashboard(): Promise<UserDashboard> {
    try {
      console.log('Fetching user dashboard');
      
      const response = await this.getCached<any>(
        '/users/me/dashboard',
        undefined,
        { timeout: 15000 }
      );
      
      return {
        summary: {
          organizationsCount: response.summary?.organizations_count || response.summary?.organizationsCount || 0,
          teamsCount: response.summary?.teams_count || response.summary?.teamsCount || 0,
          pendingInvitationsCount: response.summary?.pending_invitations_count || response.summary?.pendingInvitationsCount || 0,
          membershipsCount: response.summary?.memberships_count || response.summary?.membershipsCount || 0
        },
        recentActivity: response.recent_activity || response.recentActivity || [],
        organizations: response.organizations || [],
        teams: response.teams || [],
        pendingInvitations: response.pending_invitations || response.pendingInvitations || []
      };
    } catch (error) {
      console.error('Error fetching user dashboard:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserDashboard');
    }
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<UserStats> {
    try {
      console.log('Fetching user statistics');
      
      const dashboard = await this.getUserDashboard();
      return dashboard.summary;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserStats');
    }
  }

  // ===== USER ORGANIZATION OPERATIONS =====

  /**
   * Get user's organizations
   */
  async getUserOrganizations(): Promise<any[]> {
    try {
      console.log('Fetching user organizations');
      
      const response = await this.getCached<any[]>(
        '/users/me/organizations',
        undefined,
        { timeout: 10000 }
      );
      
      // Normalize organization data
      return (response || []).map((org: any) => ({
        _id: org.id || org._id,
        id: org.id || org._id,
        name: org.name,
        description: org.description,
        logoUrl: org.logo_url || org.logoUrl,
        membersCount: org.members_count || org.membersCount,
        teamsCount: org.teams_count || org.teamsCount,
        createdAt: org.created_at || org.createdAt,
        updatedAt: org.updated_at || org.updatedAt,
        role: org.role || 'member'
      }));
    } catch (error) {
      console.error('Error fetching user organizations:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserOrganizations');
    }
  }

  /**
   * Get user's organizations with metadata (for pagination)
   */
  async getUserOrganizationsWithMetadata(
    page?: number,
    limit?: number
  ): Promise<ApiResponse<any[]>> {
    try {
      console.log('Fetching user organizations with metadata');
      
      const params: any = {};
      if (page) params.page = page;
      if (limit) params.limit = limit;
      
      const response = await this.requestWithMetadata<any[]>(
        'GET',
        '/users/me/organizations',
        undefined,
        { params, timeout: 10000 }
      );
      
      return response;
    } catch (error) {
      console.error('Error fetching user organizations with metadata:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserOrganizationsWithMetadata');
    }
  }

  // ===== USER TEAM OPERATIONS =====

  /**
   * Get user's teams
   */
  async getUserTeams(): Promise<any[]> {
    try {
      console.log('Fetching user teams');
      
      const response = await this.getCached<any[]>(
        '/users/me/teams',
        undefined,
        { timeout: 10000 }
      );
      
      // Normalize team data
      return (response || []).map((team: any) => ({
        _id: team.id || team._id,
        id: team.id || team._id,
        name: team.name,
        description: team.description,
        organizationId: team.organization_id || team.organizationId,
        organizationName: team.organization_name || team.organizationName,
        membersCount: team.members_count || team.membersCount,
        createdAt: team.created_at || team.createdAt,
        updatedAt: team.updated_at || team.updatedAt,
        role: team.role || 'member'
      }));
    } catch (error) {
      console.error('Error fetching user teams:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserTeams');
    }
  }

  /**
   * Get user's teams with metadata (for pagination)
   */
  async getUserTeamsWithMetadata(
    page?: number,
    limit?: number
  ): Promise<ApiResponse<any[]>> {
    try {
      console.log('Fetching user teams with metadata');
      
      const params: any = {};
      if (page) params.page = page;
      if (limit) params.limit = limit;
      
      const response = await this.requestWithMetadata<any[]>(
        'GET',
        '/users/me/teams',
        undefined,
        { params, timeout: 10000 }
      );
      
      return response;
    } catch (error) {
      console.error('Error fetching user teams with metadata:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserTeamsWithMetadata');
    }
  }

  // ===== USER MEMBERSHIP OPERATIONS =====

  /**
   * Get user's memberships
   */
  async getUserMemberships(): Promise<{
    memberships: UserMembership[];
    pagination: any;
  }> {
    try {
      console.log('Fetching user memberships');
      
      const response = await this.getCached<any>('/users/me/memberships');
      
      // Handle both array and object response formats
      const membershipsArray = Array.isArray(response) ? response : (response?.data || []);
      
      const memberships: UserMembership[] = membershipsArray
        .filter((membership: any) => {
          // Filter out system-level memberships (contextType === "system")
          const contextType = membership.contextType || membership.context_type;
          return contextType !== 'system';
        })
        .map((membership: any) => {
          // Handle contextType/contextId pattern from API
          const isOrganization = membership.contextType === 'organization' || membership.context_type === 'organization';
          const isTeam = membership.contextType === 'team' || membership.context_type === 'team';
          const contextId = membership.contextId || membership.context_id;
          const contextName = membership.contextName || membership.context_name;

          return {
            id: membership.id,
            userId: membership.user_id || membership.userId,
            organizationId: isOrganization ? contextId : (membership.organization_id || membership.organizationId),
            organizationName: isOrganization ? contextName : (membership.organization_name || membership.organizationName),
            teamId: isTeam ? contextId : (membership.team_id || membership.teamId),
            teamName: isTeam ? contextName : (membership.team_name || membership.teamName),
            roleId: membership.role_id || membership.roleId,
            roleName: membership.role_name || membership.roleName,
            joinedAt: membership.joined_at || membership.joinedAt,
            invitedBy: membership.invited_by || membership.invitedBy
          };
        });
      
      return {
        memberships,
        pagination: response?.pagination || null
      };
    } catch (error) {
      console.error('Error fetching user memberships:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserMemberships');
    }
  }

  // ===== USER PERMISSION OPERATIONS =====

  /**
   * Get current user's permissions (API only supports authenticated user's own permissions)
   */
  async getUserPermissions(
    userId: string,
    scope?: {
      organizationId?: string;
      teamId?: string;
    }
  ): Promise<{ permissions: UserPermission[] }> {
    try {
      console.log(`Fetching permissions for current user (ignoring userId ${userId})`, scope);
      
      // Helper function to validate MongoDB ObjectId
      const isValidObjectId = (id: string): boolean => {
        return /^[0-9a-fA-F]{24}$/.test(id);
      };
      
      const params: Record<string, string> = {};
      if (scope?.organizationId) {
        // Validate organizationId before adding to params
        if (!isValidObjectId(scope.organizationId)) {
          console.warn(`Invalid organizationId format: ${scope.organizationId}`);
          // Return empty permissions for invalid ID instead of making a request that will fail
          return { permissions: [] };
        }
        params.context_type = 'organization';
        params.context_id = scope.organizationId;
      }
      if (scope?.teamId) {
        // Validate teamId before adding to params
        if (!isValidObjectId(scope.teamId)) {
          console.warn(`Invalid teamId format: ${scope.teamId}`);
          // Return empty permissions for invalid ID instead of making a request that will fail
          return { permissions: [] };
        }
        params.context_type = 'team';
        params.context_id = scope.teamId;
      }
      
      // API only supports /users/me/permissions
      const response = await this.getCached<any>(
        '/users/me/permissions',
        params
      );
      
      const permissions = (response.permissions || response || []).map((perm: any) => ({
        id: perm.id,
        name: perm.name,
        resource: perm.resource,
        action: perm.action,
        scope: perm.scope,
        description: perm.description
      }));
      
      return { permissions };
    } catch (error) {
      console.error(`Error fetching permissions for user ${userId}:`, error);
      throw this.errorHandler.handleError(error, `UserService.getUserPermissions(${userId})`);
    }
  }

  // ===== USER INVITATION OPERATIONS =====

  /**
   * Get user's received invitations
   */
  async getUserInvitations(
    status?: string,
    page: number = 1,
    limit: number = 10
  ): Promise<{
    data: UserInvitation[];
    pagination: any;
  }> {
    try {
      console.log('Fetching user invitations', { status, page, limit });
      
      const response = await this.get<any>('/users/me/invitations', {
        params: { status, page, limit },
        timeout: 10000
      });
      
      const invitations = (response.data || response || []).map((item: any) => {
        // The API returns { invitation: {...}, organizationName: ..., ... }
        const inv = item.invitation || item;
        
        return {
          id: inv.id,
          organization_id: inv.organizationId,
          organization_name: item.organizationName || inv.organizationName,
          team_id: inv.teamId,
          team_name: item.teamName || inv.teamName,
          invitee_email: inv.inviteeEmail,
          inviter_id: inv.inviterUserId,
          inviter_name: inv.inviterName || item.inviterName,
          role_id: inv.roleId,
          role_name: inv.roleName,
          status: inv.status,
          personal_message: inv.personalMessage,
          expires_at: inv.expiresAt,
          created_at: inv.createdAt
        };
      });
      
      return {
        data: invitations,
        pagination: response.metadata?.pagination || response.pagination || null
      };
    } catch (error) {
      console.error('Error fetching user invitations:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserInvitations');
    }
  }

  /**
   * Get user's sent invitations
   */
  async getUserSentInvitations(
    status?: string,
    page: number = 1,
    limit: number = 10
  ): Promise<{
    data: UserInvitation[];
    pagination: any;
  }> {
    try {
      console.log('Fetching user sent invitations', { status, page, limit });
      
      const response = await this.get<any>('/users/me/sent-invitations', {
        params: { status, page, limit },
        timeout: 10000
      });
      
      return {
        data: response.data || response || [],
        pagination: response.pagination || null
      };
    } catch (error) {
      console.error('Error fetching user sent invitations:', error);
      throw this.errorHandler.handleError(error, 'UserService.getUserSentInvitations');
    }
  }

  /**
   * Get user's content rating
   */
  async getUserContentRating(contentId: string): Promise<any | null> {
    try {
      console.log(`Getting user rating for content: ${contentId}`);
      
      const response = await this.get<any>(
        `/content/${contentId}/user-rating`,
        { timeout: 10000 }
      );
      
      return response;
    } catch (error: any) {
      // If the error is 404, return null (no rating exists)
      if (error.status === 404 || error.response?.status === 404) {
        return null;
      }
      console.error(`Error getting user content rating for ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `UserService.getUserContentRating(${contentId})`);
    }
  }

  // ===== ACCOUNT MANAGEMENT =====

  /**
   * Validate if user can be deleted
   * Checks if user is last admin of any organizations or teams
   */
  async validateUserDeletion(): Promise<UserDeletionValidation> {
    try {
      console.log('Validating user deletion');

      const response = await this.get<UserDeletionValidation>(
        '/users/me/validate-deletion',
        { timeout: 10000 }
      );

      console.log('User deletion validation result:', response);
      return response;
    } catch (error) {
      console.error('Error validating user deletion:', error);
      throw this.errorHandler.handleError(error, 'UserService.validateUserDeletion');
    }
  }

  /**
   * Delete user account with cascade
   * Removes user from all organizations/teams and deletes account
   */
  async deleteAccount(): Promise<{ success: boolean; message: string; deletedEntities: { organizationMemberships: number; teamMemberships: number } }> {
    try {
      console.log('Deleting user account');

      const response = await this.delete<{ success: boolean; message: string; deletedEntities: { organizationMemberships: number; teamMemberships: number } }>(
        '/users/me',
        { timeout: 30000 } // Longer timeout for cascade deletion
      );

      console.log('User account deleted:', response);
      return response;
    } catch (error) {
      console.error('Error deleting user account:', error);
      throw this.errorHandler.handleError(error, 'UserService.deleteAccount');
    }
  }

  // ===== CONTENT AFFIRMATION OPERATIONS =====

  /**
   * Get content affirmation status
   * Checks if user needs to (re)affirm content upload responsibility
   */
  async getContentAffirmationStatus(): Promise<ContentAffirmationStatus> {
    try {
      console.log('Fetching content affirmation status');

      const response = await this.get<ContentAffirmationStatus>(
        '/users/me/content-affirmation-status',
        { timeout: 10000 }
      );

      console.log('Content affirmation status:', response);
      return response;
    } catch (error) {
      console.error('Error fetching content affirmation status:', error);
      throw this.errorHandler.handleError(error, 'UserService.getContentAffirmationStatus');
    }
  }

  /**
   * Affirm content upload responsibility
   * Sets affirmation date to now and current terms version
   */
  async affirmContentResponsibility(termsVersion: string): Promise<{ success: boolean; user: User }> {
    try {
      console.log('Affirming content responsibility', { termsVersion });

      const response = await this.updateProfile({
        contentAffirmationDate: new Date().toISOString(),
        contentAffirmationTermsVersion: termsVersion
      });

      console.log('Content responsibility affirmed');
      return response;
    } catch (error) {
      console.error('Error affirming content responsibility:', error);
      throw this.errorHandler.handleError(error, 'UserService.affirmContentResponsibility');
    }
  }

  /**
   * Rescind content affirmation
   * Clears affirmation date and terms version
   */
  async rescindContentAffirmation(): Promise<{ success: boolean; user: User }> {
    try {
      console.log('Rescinding content affirmation');

      const response = await this.updateProfile({
        contentAffirmationDate: null,
        contentAffirmationTermsVersion: null
      });

      console.log('Content affirmation rescinded');
      return response;
    } catch (error) {
      console.error('Error rescinding content affirmation:', error);
      throw this.errorHandler.handleError(error, 'UserService.rescindContentAffirmation');
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Normalize user data from API response
   * IMPORTANT: This must preserve ALL user state fields to prevent data loss
   * when updating the user in AuthContext
   */
  private normalizeUser(userData: any): User {
    return {
      id: userData.id || userData._id,
      email: userData.email,
      name: userData.name,
      bio: userData.bio,
      avatar: userData.avatar || userData.avatar_url || userData.avatarUrl,
      phone_number: userData.phone_number || userData.phoneNumber,
      role: userData.role,
      is_email_verified: userData.is_email_verified || userData.isEmailVerified,
      two_factor_enabled: userData.two_factor_enabled || userData.twoFactorEnabled || userData.two_factor_enabled,
      created_at: userData.created_at || userData.createdAt,
      updated_at: userData.updated_at || userData.updatedAt
    };
  }
}

// Export singleton instance
export const userService = new UserService();